import { z } from 'zod';

import { galleryImageWireSchema } from '../image';
import { orderAdminSchema, orderOwnerSchema } from '../order';
import { restaurantPublicSchema } from '../restaurant';
import { reviewSelfSchema } from '../review';
import { savedUserAddressSelfSchema, userSelfSchema } from '../user';
import {
  createVoucherResponseSchema,
  listVouchersResponseSchema,
  ownerCreateVoucherRequestSchema,
  patchVoucherEnabledRequestSchema,
  patchVoucherEnabledResponseSchema,
} from '../voucher';

import * as ApiAdmin from './admin';
import * as ApiImages from './images';
import * as ApiMe from './me';
import * as ApiOrders from './orders';
import * as ApiPaymentProviders from './paymentProviders';
import * as ApiPayments from './payments';
import * as ApiRestaurantBackoffice from './restaurantBackoffice';
import * as ApiRestaurantMembers from './restaurantMembers';
import * as ApiRestaurants from './restaurants';
import * as ApiReviews from './reviews';

export type ApiHttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ApiAuthMode = 'none' | 'cognito' | 'cognito-or-guest';

/** How the SDK interprets a successful HTTP response (default: JSON body). */
export type ApiResponseShape = 'json' | 'noContent';

export interface ApiEndpointDefinition<
  M extends ApiHttpMethod,
  Path extends string,
  Response extends z.ZodTypeAny,
  Params extends z.ZodTypeAny | undefined = undefined,
  Query extends z.ZodTypeAny | undefined = undefined,
  WireQuery extends z.ZodTypeAny | undefined = undefined,
  Body extends z.ZodTypeAny | undefined = undefined,
> {
  readonly method: M;
  readonly path: Path;
  readonly response: Response;
  readonly auth: ApiAuthMode;
  readonly responseShape?: ApiResponseShape;
  readonly params?: Params;
  readonly query?: Query;
  readonly wireQuery?: WireQuery;
  readonly request?: Body;
  readonly serializeQuery?: Query extends z.ZodTypeAny
    ? (q: z.input<Query>) => Record<string, string | undefined>
    : never;
}

export const apiContract = {
  listPublicRestaurants: {
    method: 'GET',
    path: '/v1/restaurants',
    query: ApiRestaurants.publicRestaurantsClientQuerySchema,
    wireQuery: ApiRestaurants.publicRestaurantsListWireQuerySchema,
    serializeQuery: ApiRestaurants.serializePublicRestaurantsClientQuery,
    response: ApiRestaurants.publicRestaurantsListResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants',
    typeof ApiRestaurants.publicRestaurantsListResponseSchema,
    undefined,
    typeof ApiRestaurants.publicRestaurantsClientQuerySchema,
    typeof ApiRestaurants.publicRestaurantsListWireQuerySchema
  >,

  getPublicRestaurantPointer: {
    method: 'GET',
    path: '/v1/restaurants/:slug',
    params: ApiRestaurants.restaurantPathParamsSchema,
    response: restaurantPublicSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants/:slug',
    typeof restaurantPublicSchema,
    typeof ApiRestaurants.restaurantPathParamsSchema
  >,

  getPublicMenuPointer: {
    method: 'GET',
    path: '/v1/restaurants/:slug/menu',
    params: ApiRestaurants.restaurantPathParamsSchema,
    response: ApiRestaurants.menuListResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants/:slug/menu',
    typeof ApiRestaurants.menuListResponseSchema,
    typeof ApiRestaurants.restaurantPathParamsSchema
  >,

  listPublicRestaurantReviews: {
    method: 'GET',
    path: '/v1/restaurants/:slug/reviews',
    params: ApiRestaurants.restaurantPathParamsSchema,
    wireQuery: ApiReviews.publicReviewsListQuerySchema,
    response: ApiReviews.publicReviewsListResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants/:slug/reviews',
    typeof ApiReviews.publicReviewsListResponseSchema,
    typeof ApiRestaurants.restaurantPathParamsSchema,
    undefined,
    typeof ApiReviews.publicReviewsListQuerySchema
  >,

  getPublicRestaurantDeliveryPricing: {
    method: 'GET',
    path: '/v1/restaurants/:slug/delivery-pricing',
    params: ApiRestaurants.restaurantPathParamsSchema,
    response: ApiRestaurants.publicDeliveryPricingResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants/:slug/delivery-pricing',
    typeof ApiRestaurants.publicDeliveryPricingResponseSchema,
    typeof ApiRestaurants.restaurantPathParamsSchema
  >,

  listPaymentProviders: {
    method: 'GET',
    path: '/v1/payment-providers',
    response: ApiPaymentProviders.paymentProvidersResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/payment-providers',
    typeof ApiPaymentProviders.paymentProvidersResponseSchema
  >,

  reverseGeocode: {
    method: 'GET',
    path: '/v1/geocode/reverse',
    wireQuery: ApiRestaurants.reverseGeocodeQuerySchema,
    response: ApiRestaurants.geocodeAddressResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/geocode/reverse',
    typeof ApiRestaurants.geocodeAddressResponseSchema,
    undefined,
    undefined,
    typeof ApiRestaurants.reverseGeocodeQuerySchema
  >,

  forwardGeocode: {
    method: 'GET',
    path: '/v1/geocode/forward',
    wireQuery: ApiRestaurants.forwardGeocodeQuerySchema,
    response: ApiRestaurants.geocodeAddressResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/geocode/forward',
    typeof ApiRestaurants.geocodeAddressResponseSchema,
    undefined,
    undefined,
    typeof ApiRestaurants.forwardGeocodeQuerySchema
  >,

  suggestGeocode: {
    method: 'GET',
    path: '/v1/geocode/suggest',
    wireQuery: ApiRestaurants.forwardGeocodeQuerySchema,
    response: ApiRestaurants.geocodeSuggestResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/geocode/suggest',
    typeof ApiRestaurants.geocodeSuggestResponseSchema,
    undefined,
    undefined,
    typeof ApiRestaurants.forwardGeocodeQuerySchema
  >,

  postOrderPricingPreview: {
    method: 'POST',
    path: '/v1/orders/pricing-preview',
    request: ApiOrders.orderPricingPreviewRequestSchema,
    response: ApiOrders.orderPricingPreviewResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/orders/pricing-preview',
    typeof ApiOrders.orderPricingPreviewResponseSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiOrders.orderPricingPreviewRequestSchema
  >,

  placeOrder: {
    method: 'POST',
    path: '/v1/orders',
    request: ApiOrders.placeOrderRequestSchema,
    response: ApiOrders.placeOrderResponseSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/orders',
    typeof ApiOrders.placeOrderResponseSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiOrders.placeOrderRequestSchema
  >,

  getMe: {
    method: 'GET',
    path: '/v1/me',
    response: userSelfSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<'GET', '/v1/me', typeof userSelfSchema>,

  patchMe: {
    method: 'PATCH',
    path: '/v1/me',
    request: ApiMe.patchMeRequestSchema,
    response: userSelfSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    '/v1/me',
    typeof userSelfSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiMe.patchMeRequestSchema
  >,

  listMeAddresses: {
    method: 'GET',
    path: '/v1/me/addresses',
    response: ApiMe.meAddressesListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/me/addresses',
    typeof ApiMe.meAddressesListResponseSchema
  >,

  postMeAddress: {
    method: 'POST',
    path: '/v1/me/addresses',
    request: ApiMe.postSavedAddressRequestSchema,
    response: savedUserAddressSelfSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/me/addresses',
    typeof savedUserAddressSelfSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiMe.postSavedAddressRequestSchema
  >,

  patchMeAddress: {
    method: 'PATCH',
    path: '/v1/me/addresses/:addressId',
    params: ApiMe.savedAddressPathParamsSchema,
    request: ApiMe.patchSavedAddressRequestSchema,
    response: savedUserAddressSelfSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof savedUserAddressSelfSchema,
    typeof ApiMe.savedAddressPathParamsSchema,
    undefined,
    undefined,
    typeof ApiMe.patchSavedAddressRequestSchema
  >,

  deleteMeAddress: {
    method: 'DELETE',
    path: '/v1/me/addresses/:addressId',
    params: ApiMe.savedAddressPathParamsSchema,
    response: userSelfSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    typeof userSelfSchema,
    typeof ApiMe.savedAddressPathParamsSchema
  >,

  postMeUpgrade: {
    method: 'POST',
    path: '/v1/me/upgrade',
    request: ApiMe.postMeUpgradeRequestSchema,
    response: ApiMe.postMeUpgradeResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/me/upgrade',
    typeof ApiMe.postMeUpgradeResponseSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiMe.postMeUpgradeRequestSchema
  >,

  listMeRestaurants: {
    method: 'GET',
    path: '/v1/me/restaurants',
    response: ApiMe.meRestaurantsResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/me/restaurants',
    typeof ApiMe.meRestaurantsResponseSchema
  >,

  getMeStats: {
    method: 'GET',
    path: '/v1/me/stats',
    response: ApiMe.meStatsResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<'GET', '/v1/me/stats', typeof ApiMe.meStatsResponseSchema>,

  getOrder: {
    method: 'GET',
    path: '/v1/orders/:id',
    params: ApiOrders.orderCustomerPathParamsSchema,
    response: ApiOrders.orderCustomerResponseSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/orders/:id',
    typeof ApiOrders.orderCustomerResponseSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema
  >,

  listCustomerOrders: {
    method: 'GET',
    path: '/v1/orders',
    wireQuery: ApiOrders.customerOrdersWireQuerySchema,
    response: ApiOrders.orderCustomerListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/orders',
    typeof ApiOrders.orderCustomerListResponseSchema,
    undefined,
    undefined,
    typeof ApiOrders.customerOrdersWireQuerySchema
  >,

  cancelOrder: {
    method: 'POST',
    path: '/v1/orders/:id/cancel',
    params: ApiOrders.orderCustomerPathParamsSchema,
    response: ApiOrders.orderCustomerResponseSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiOrders.orderCustomerResponseSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema
  >,

  disputeOrder: {
    method: 'POST',
    path: '/v1/orders/:id/dispute',
    params: ApiOrders.orderCustomerPathParamsSchema,
    request: ApiOrders.customerDisputeRequestSchema,
    response: ApiOrders.orderCustomerResponseSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/orders/:id/dispute',
    typeof ApiOrders.orderCustomerResponseSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema,
    undefined,
    undefined,
    typeof ApiOrders.customerDisputeRequestSchema
  >,

  getOrderReview: {
    method: 'GET',
    path: '/v1/orders/:id/review',
    params: ApiOrders.orderCustomerPathParamsSchema,
    response: ApiOrders.orderReviewSelfResponseSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/orders/:id/review',
    typeof ApiOrders.orderReviewSelfResponseSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema
  >,

  submitOrderReview: {
    method: 'POST',
    path: '/v1/orders/:id/review',
    params: ApiOrders.orderCustomerPathParamsSchema,
    request: ApiReviews.submitReviewRequestSchema,
    response: reviewSelfSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/orders/:id/review',
    typeof reviewSelfSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.submitReviewRequestSchema
  >,

  editOrderReview: {
    method: 'PATCH',
    path: '/v1/orders/:id/review',
    params: ApiOrders.orderCustomerPathParamsSchema,
    request: ApiReviews.editReviewRequestSchema,
    response: reviewSelfSchema,
    auth: 'cognito-or-guest',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    '/v1/orders/:id/review',
    typeof reviewSelfSchema,
    typeof ApiOrders.orderCustomerPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.editReviewRequestSchema
  >,

  deleteOrderReview: {
    method: 'DELETE',
    path: '/v1/orders/:id/review',
    params: ApiOrders.orderCustomerPathParamsSchema,
    response: z.undefined(),
    auth: 'cognito-or-guest',
    responseShape: 'noContent',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    '/v1/orders/:id/review',
    z.ZodUndefined,
    typeof ApiOrders.orderCustomerPathParamsSchema
  >,

  listRestaurantOwnerOrders: {
    method: 'GET',
    path: '/v1/restaurants/:id/orders',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    wireQuery: ApiOrders.restaurantOwnerOrdersWireQuerySchema,
    response: ApiOrders.orderOwnerListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/restaurants/:id/orders',
    typeof ApiOrders.orderOwnerListResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    typeof ApiOrders.restaurantOwnerOrdersWireQuerySchema
  >,

  confirmRestaurantOrder: {
    method: 'POST',
    path: '/v1/restaurants/:id/orders/:orderId/confirm',
    params: ApiOrders.restaurantAndOrderPathParamsSchema,
    response: orderOwnerSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof orderOwnerSchema,
    typeof ApiOrders.restaurantAndOrderPathParamsSchema
  >,

  rejectRestaurantOrder: {
    method: 'POST',
    path: '/v1/restaurants/:id/orders/:orderId/reject',
    params: ApiOrders.restaurantAndOrderPathParamsSchema,
    response: orderOwnerSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof orderOwnerSchema,
    typeof ApiOrders.restaurantAndOrderPathParamsSchema
  >,

  advanceRestaurantOrder: {
    method: 'POST',
    path: '/v1/restaurants/:id/orders/:orderId/advance',
    params: ApiOrders.restaurantAndOrderPathParamsSchema,
    request: ApiOrders.restaurantOrderAdvanceRequestSchema,
    response: orderOwnerSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof orderOwnerSchema,
    typeof ApiOrders.restaurantAndOrderPathParamsSchema,
    undefined,
    undefined,
    typeof ApiOrders.restaurantOrderAdvanceRequestSchema
  >,

  respondRestaurantOrderDispute: {
    method: 'POST',
    path: '/v1/restaurants/:id/orders/:orderId/disputes/respond',
    params: ApiOrders.restaurantAndOrderPathParamsSchema,
    request: ApiOrders.restaurantDisputeResponseRequestSchema,
    response: orderOwnerSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof orderOwnerSchema,
    typeof ApiOrders.restaurantAndOrderPathParamsSchema,
    undefined,
    undefined,
    typeof ApiOrders.restaurantDisputeResponseRequestSchema
  >,

  flagRestaurantReview: {
    method: 'POST',
    path: '/v1/restaurants/:id/reviews/:reviewId/flag',
    params: ApiReviews.restaurantReviewPathParamsSchema,
    request: ApiReviews.flagReviewRequestSchema,
    response: ApiReviews.reviewFlagResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiReviews.reviewFlagResponseSchema,
    typeof ApiReviews.restaurantReviewPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.flagReviewRequestSchema
  >,

  postRestaurantReviewResponse: {
    method: 'POST',
    path: '/v1/restaurants/:id/reviews/:reviewId/response',
    params: ApiReviews.restaurantReviewPathParamsSchema,
    request: ApiReviews.restaurantResponseRequestSchema,
    response: ApiReviews.restaurantReviewResponseMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiReviews.restaurantReviewResponseMutationResponseSchema,
    typeof ApiReviews.restaurantReviewPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.restaurantResponseRequestSchema
  >,

  patchRestaurantReviewResponse: {
    method: 'PATCH',
    path: '/v1/restaurants/:id/reviews/:reviewId/response',
    params: ApiReviews.restaurantReviewPathParamsSchema,
    request: ApiReviews.restaurantResponseRequestSchema,
    response: ApiReviews.restaurantReviewResponseMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof ApiReviews.restaurantReviewResponseMutationResponseSchema,
    typeof ApiReviews.restaurantReviewPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.restaurantResponseRequestSchema
  >,

  deleteRestaurantReviewResponse: {
    method: 'DELETE',
    path: '/v1/restaurants/:id/reviews/:reviewId/response',
    params: ApiReviews.restaurantReviewPathParamsSchema,
    response: z.undefined(),
    auth: 'cognito',
    responseShape: 'noContent',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    z.ZodUndefined,
    typeof ApiReviews.restaurantReviewPathParamsSchema
  >,

  listRestaurantMembers: {
    method: 'GET',
    path: '/v1/restaurants/:id/members',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurantMembers.listRestaurantMembersResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof ApiRestaurantMembers.listRestaurantMembersResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  postRestaurantMemberInvite: {
    method: 'POST',
    path: '/v1/restaurants/:id/members',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantMembers.postRestaurantMemberInviteRequestSchema,
    response: ApiRestaurantMembers.postRestaurantMemberInviteResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurantMembers.postRestaurantMemberInviteResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantMembers.postRestaurantMemberInviteRequestSchema
  >,

  deleteRestaurantMember: {
    method: 'DELETE',
    path: '/v1/restaurants/:id/members/:userId',
    params: ApiRestaurantMembers.restaurantMemberPathParamsSchema,
    response: z.undefined(),
    auth: 'cognito',
    responseShape: 'noContent',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    z.ZodUndefined,
    typeof ApiRestaurantMembers.restaurantMemberPathParamsSchema
  >,

  listRestaurantPayouts: {
    method: 'GET',
    path: '/v1/restaurants/:id/payouts',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurantMembers.listRestaurantPayoutsResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof ApiRestaurantMembers.listRestaurantPayoutsResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  listRestaurantDisputes: {
    method: 'GET',
    path: '/v1/restaurants/:id/disputes',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    wireQuery: ApiRestaurantMembers.listRestaurantDisputesWireQuerySchema,
    response: ApiOrders.orderOwnerListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof ApiOrders.orderOwnerListResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    typeof ApiRestaurantMembers.listRestaurantDisputesWireQuerySchema
  >,

  onboardRestaurant: {
    method: 'POST',
    path: '/v1/restaurants/onboard',
    request: ApiRestaurantBackoffice.restaurantOnboardRequestSchema,
    response: ApiRestaurantBackoffice.restaurantOnboardResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/restaurants/onboard',
    typeof ApiRestaurantBackoffice.restaurantOnboardResponseSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.restaurantOnboardRequestSchema
  >,

  patchRestaurantBackoffice: {
    method: 'PATCH',
    path: '/v1/restaurants/:id',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.patchRestaurantRequestSchema,
    response: ApiRestaurantBackoffice.patchRestaurantResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof ApiRestaurantBackoffice.patchRestaurantResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.patchRestaurantRequestSchema
  >,

  postRestaurantMenuItem: {
    method: 'POST',
    path: '/v1/restaurants/:id/menu-items',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.postMenuItemRequestSchema,
    response: ApiRestaurantBackoffice.postMenuItemResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurantBackoffice.postMenuItemResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.postMenuItemRequestSchema
  >,

  getRestaurantOwnerDetail: {
    method: 'GET',
    path: '/v1/restaurants/:id/owner',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurantBackoffice.restaurantOwnerDetailResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof ApiRestaurantBackoffice.restaurantOwnerDetailResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  postRestaurantMenuCategory: {
    method: 'POST',
    path: '/v1/restaurants/:id/menu-categories',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.postMenuCategoryRequestSchema,
    response: ApiRestaurantBackoffice.postMenuCategoryResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurantBackoffice.postMenuCategoryResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.postMenuCategoryRequestSchema
  >,

  patchRestaurantMenuCategory: {
    method: 'PATCH',
    path: '/v1/restaurants/:id/menu-categories/:categoryId',
    params: ApiRestaurantBackoffice.menuCategoryPathParamsSchema,
    request: ApiRestaurantBackoffice.patchMenuCategoryRequestSchema,
    response: ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    typeof ApiRestaurantBackoffice.menuCategoryPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.patchMenuCategoryRequestSchema
  >,

  deleteRestaurantMenuCategory: {
    method: 'DELETE',
    path: '/v1/restaurants/:id/menu-categories/:categoryId',
    params: ApiRestaurantBackoffice.menuCategoryPathParamsSchema,
    response: ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    typeof ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    typeof ApiRestaurantBackoffice.menuCategoryPathParamsSchema
  >,

  putRestaurantMenuCategoriesOrder: {
    method: 'PUT',
    path: '/v1/restaurants/:id/menu-categories/order',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.putMenuCategoriesOrderRequestSchema,
    response: ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PUT',
    string,
    typeof ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.putMenuCategoriesOrderRequestSchema
  >,

  patchRestaurantMenuItem: {
    method: 'PATCH',
    path: '/v1/restaurants/:id/menu-items/:menuItemId',
    params: ApiRestaurantBackoffice.menuItemPathParamsSchema,
    request: ApiRestaurantBackoffice.patchMenuItemRequestSchema,
    response: ApiRestaurantBackoffice.patchMenuItemResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof ApiRestaurantBackoffice.patchMenuItemResponseSchema,
    typeof ApiRestaurantBackoffice.menuItemPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.patchMenuItemRequestSchema
  >,

  deleteRestaurantMenuItem: {
    method: 'DELETE',
    path: '/v1/restaurants/:id/menu-items/:menuItemId',
    params: ApiRestaurantBackoffice.menuItemPathParamsSchema,
    response: ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    typeof ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    typeof ApiRestaurantBackoffice.menuItemPathParamsSchema
  >,

  putRestaurantMenuItemsOrder: {
    method: 'PUT',
    path: '/v1/restaurants/:id/menu-items/order',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.putMenuItemsOrderRequestSchema,
    response: ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PUT',
    string,
    typeof ApiRestaurantBackoffice.menuMutationVersionResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.putMenuItemsOrderRequestSchema
  >,

  putRestaurantOpeningHours: {
    method: 'PUT',
    path: '/v1/restaurants/:id/opening-hours',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.putOpeningHoursRequestSchema,
    response: ApiRestaurantBackoffice.putOpeningHoursResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PUT',
    string,
    typeof ApiRestaurantBackoffice.putOpeningHoursResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.putOpeningHoursRequestSchema
  >,

  putRestaurantDeliveryPricing: {
    method: 'PUT',
    path: '/v1/restaurants/:id/delivery-pricing',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.putDeliveryPricingRequestSchema,
    response: ApiRestaurantBackoffice.putDeliveryPricingResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PUT',
    string,
    typeof ApiRestaurantBackoffice.putDeliveryPricingResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.putDeliveryPricingRequestSchema
  >,

  submitRestaurantForReview: {
    method: 'POST',
    path: '/v1/restaurants/:id/submit-for-review',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurantBackoffice.submitForReviewResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurantBackoffice.submitForReviewResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  pauseRestaurantOwner: {
    method: 'POST',
    path: '/v1/restaurants/:id/pause',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurants.restaurantOwnerLifecycleResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantOwnerLifecycleResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  resumeRestaurantOwner: {
    method: 'POST',
    path: '/v1/restaurants/:id/resume',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: ApiRestaurants.restaurantOwnerLifecycleResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantOwnerLifecycleResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  putRestaurantOwnerPayoutConfig: {
    method: 'PUT',
    path: '/v1/restaurants/:id/payout-config',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiRestaurantBackoffice.putOwnerPayoutConfigRequestSchema,
    response: ApiRestaurantBackoffice.putOwnerPayoutConfigResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PUT',
    string,
    typeof ApiRestaurantBackoffice.putOwnerPayoutConfigResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiRestaurantBackoffice.putOwnerPayoutConfigRequestSchema
  >,

  listOwnerRestaurantVouchers: {
    method: 'GET',
    path: '/v1/restaurants/:id/vouchers',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    response: listVouchersResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof listVouchersResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema
  >,

  postOwnerRestaurantVoucher: {
    method: 'POST',
    path: '/v1/restaurants/:id/vouchers',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ownerCreateVoucherRequestSchema,
    response: createVoucherResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof createVoucherResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ownerCreateVoucherRequestSchema
  >,

  patchOwnerRestaurantVoucher: {
    method: 'PATCH',
    path: '/v1/restaurants/:id/vouchers/:voucherId',
    params: ApiRestaurantBackoffice.ownerRestaurantVoucherPathParamsSchema,
    request: patchVoucherEnabledRequestSchema,
    response: patchVoucherEnabledResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof patchVoucherEnabledResponseSchema,
    typeof ApiRestaurantBackoffice.ownerRestaurantVoucherPathParamsSchema,
    undefined,
    undefined,
    typeof patchVoucherEnabledRequestSchema
  >,

  listAdminRestaurants: {
    method: 'GET',
    path: '/v1/admin/restaurants',
    wireQuery: ApiAdmin.adminRestaurantListWireQuerySchema,
    response: ApiAdmin.adminRestaurantListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/admin/restaurants',
    typeof ApiAdmin.adminRestaurantListResponseSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminRestaurantListWireQuerySchema
  >,

  listAdminPlatformVouchers: {
    method: 'GET',
    path: '/v1/admin/vouchers',
    response: listVouchersResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<'GET', '/v1/admin/vouchers', typeof listVouchersResponseSchema>,

  postAdminPlatformVoucher: {
    method: 'POST',
    path: '/v1/admin/vouchers',
    request: ApiAdmin.adminCreateVoucherRequestSchema,
    response: createVoucherResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/admin/vouchers',
    typeof createVoucherResponseSchema,
    undefined,
    undefined,
    undefined,
    typeof ApiAdmin.adminCreateVoucherRequestSchema
  >,

  patchAdminPlatformVoucher: {
    method: 'PATCH',
    path: '/v1/admin/vouchers/:voucherId',
    params: ApiAdmin.adminPlatformVoucherPathParamsSchema,
    request: patchVoucherEnabledRequestSchema,
    response: patchVoucherEnabledResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof patchVoucherEnabledResponseSchema,
    typeof ApiAdmin.adminPlatformVoucherPathParamsSchema,
    undefined,
    undefined,
    typeof patchVoucherEnabledRequestSchema
  >,

  listAdminRestaurantVouchers: {
    method: 'GET',
    path: '/v1/admin/restaurants/:id/vouchers',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    response: listVouchersResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    string,
    typeof listVouchersResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema
  >,

  postAdminRestaurantVoucher: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/vouchers',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    request: ApiAdmin.adminCreateVoucherRequestSchema,
    response: createVoucherResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof createVoucherResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminCreateVoucherRequestSchema
  >,

  patchAdminRestaurantVoucher: {
    method: 'PATCH',
    path: '/v1/admin/restaurants/:id/vouchers/:voucherId',
    params: ApiAdmin.adminRestaurantVoucherPathParamsSchema,
    request: patchVoucherEnabledRequestSchema,
    response: patchVoucherEnabledResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof patchVoucherEnabledResponseSchema,
    typeof ApiAdmin.adminRestaurantVoucherPathParamsSchema,
    undefined,
    undefined,
    typeof patchVoucherEnabledRequestSchema
  >,

  approveAdminRestaurant: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/approve',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    response: ApiRestaurants.restaurantStatusMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantStatusMutationResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema
  >,

  rejectAdminRestaurant: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/reject',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    request: ApiAdmin.adminRejectRestaurantRequestSchema,
    response: ApiRestaurants.restaurantStatusMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantStatusMutationResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminRejectRestaurantRequestSchema
  >,

  suspendAdminRestaurant: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/suspend',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    response: ApiRestaurants.restaurantStatusMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantStatusMutationResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema
  >,

  unsuspendAdminRestaurant: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/unsuspend',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    response: ApiRestaurants.restaurantStatusMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiRestaurants.restaurantStatusMutationResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema
  >,

  runAdminRestaurantPayout: {
    method: 'POST',
    path: '/v1/admin/restaurants/:id/payouts/run',
    params: ApiAdmin.adminRestaurantIdPathParamsSchema,
    request: ApiAdmin.adminPayoutRunRequestSchema,
    response: ApiAdmin.payoutRunResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiAdmin.payoutRunResponseSchema,
    typeof ApiAdmin.adminRestaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminPayoutRunRequestSchema
  >,

  listAdminDisputes: {
    method: 'GET',
    path: '/v1/admin/disputes',
    wireQuery: ApiAdmin.adminDisputeListWireQuerySchema,
    response: ApiAdmin.adminDisputeListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/admin/disputes',
    typeof ApiAdmin.adminDisputeListResponseSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminDisputeListWireQuerySchema
  >,

  resolveAdminOrderDispute: {
    method: 'POST',
    path: '/v1/admin/orders/:id/disputes/resolve',
    params: ApiAdmin.adminOrderDisputePathParamsSchema,
    request: ApiOrders.adminDisputeResolveRequestSchema,
    response: orderAdminSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof orderAdminSchema,
    typeof ApiAdmin.adminOrderDisputePathParamsSchema,
    undefined,
    undefined,
    typeof ApiOrders.adminDisputeResolveRequestSchema
  >,

  listAdminFlaggedReviews: {
    method: 'GET',
    path: '/v1/admin/reviews/flagged',
    wireQuery: ApiAdmin.adminFlaggedReviewsWireQuerySchema,
    response: ApiReviews.flaggedReviewsListResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/admin/reviews/flagged',
    typeof ApiReviews.flaggedReviewsListResponseSchema,
    undefined,
    undefined,
    typeof ApiAdmin.adminFlaggedReviewsWireQuerySchema
  >,

  hideAdminReview: {
    method: 'POST',
    path: '/v1/admin/reviews/:reviewId/hide',
    params: ApiReviews.reviewPathParamsSchema,
    request: ApiReviews.adminHideReviewRequestSchema,
    response: ApiReviews.adminReviewMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiReviews.adminReviewMutationResponseSchema,
    typeof ApiReviews.reviewPathParamsSchema,
    undefined,
    undefined,
    typeof ApiReviews.adminHideReviewRequestSchema
  >,

  unhideAdminReview: {
    method: 'POST',
    path: '/v1/admin/reviews/:reviewId/unhide',
    params: ApiReviews.reviewPathParamsSchema,
    response: ApiReviews.adminReviewMutationResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiReviews.adminReviewMutationResponseSchema,
    typeof ApiReviews.reviewPathParamsSchema
  >,

  getAdminStats: {
    method: 'GET',
    path: '/v1/admin/stats',
    response: ApiAdmin.adminStatsResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'GET',
    '/v1/admin/stats',
    typeof ApiAdmin.adminStatsResponseSchema
  >,

  requestRestaurantImageUploadUrl: {
    method: 'POST',
    path: '/v1/restaurants/:id/images/upload-url',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiImages.requestUploadUrlRequestSchema,
    response: ApiImages.requestUploadUrlResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiImages.requestUploadUrlResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiImages.requestUploadUrlRequestSchema
  >,

  attachRestaurantImage: {
    method: 'POST',
    path: '/v1/restaurants/:id/images',
    params: ApiRestaurants.restaurantIdPathParamsSchema,
    request: ApiImages.attachImageRequestSchema,
    response: ApiImages.attachImageResponseSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'POST',
    string,
    typeof ApiImages.attachImageResponseSchema,
    typeof ApiRestaurants.restaurantIdPathParamsSchema,
    undefined,
    undefined,
    typeof ApiImages.attachImageRequestSchema
  >,

  patchRestaurantImage: {
    method: 'PATCH',
    path: '/v1/restaurants/:id/images/:imageId',
    params: ApiImages.restaurantImagePathParamsSchema,
    request: ApiImages.patchImageRequestSchema,
    response: galleryImageWireSchema,
    auth: 'cognito',
  } satisfies ApiEndpointDefinition<
    'PATCH',
    string,
    typeof galleryImageWireSchema,
    typeof ApiImages.restaurantImagePathParamsSchema,
    undefined,
    undefined,
    typeof ApiImages.patchImageRequestSchema
  >,

  deleteRestaurantImage: {
    method: 'DELETE',
    path: '/v1/restaurants/:id/images/:imageId',
    params: ApiImages.restaurantImagePathParamsSchema,
    response: z.undefined(),
    auth: 'cognito',
    responseShape: 'noContent',
  } satisfies ApiEndpointDefinition<
    'DELETE',
    string,
    z.ZodUndefined,
    typeof ApiImages.restaurantImagePathParamsSchema
  >,

  sentooWebhook: {
    method: 'POST',
    path: '/v1/payments/webhooks/sentoo',
    response: ApiPayments.sentooWebhookResponseSchema,
    auth: 'none',
  } satisfies ApiEndpointDefinition<
    'POST',
    '/v1/payments/webhooks/sentoo',
    typeof ApiPayments.sentooWebhookResponseSchema
  >,
} as const;

export type ApiContract = typeof apiContract;
export type ApiContractKey = keyof ApiContract;

/** API Gateway HTTP API v2 route key for dispatch tables. */
export function apiGatewayRouteKey(method: ApiHttpMethod, pathTemplate: string): string {
  const gw = pathTemplate
    .replaceAll(':id', '{id}')
    .replaceAll(':version', '{version}')
    .replaceAll(':orderId', '{orderId}')
    .replaceAll(':reviewId', '{reviewId}')
    .replaceAll(':imageId', '{imageId}')
    .replaceAll(':voucherId', '{voucherId}')
    .replaceAll(':addressId', '{addressId}')
    .replaceAll(':categoryId', '{categoryId}')
    .replaceAll(':menuItemId', '{menuItemId}')
    .replaceAll(':userId', '{userId}');
  return `${method} ${gw}`;
}
